<template lang="pug">
  yz-card.pa-6(outlined)
    .yz-heading-9 {{ $t('panel.visits') }}
    .yz-regular-body-xs.mb-5 {{ $t('panel.visits_description') }}

    // Error State
    div(v-if="isError")
      yz-empty-state-card(icon="mdi-alert-circle-outline" icon-color="yz-danger-500" :title="$t('error_message.unable_to_load_data')" :description="$t('error_message.please_try_again')" action-label="Retry" @action-click="fetchPanelVisits")

    // Empty State
    div(v-else-if="!isLoadingVisits && visitsData.length === 0")
      yz-empty-state-card(icon="$yzi-medical-cross" icon-color="yz-neutral-500" border-color="yz-neutral-400" :title="$t('panel.no_visits_recorded')" :description="$t('panel.visits_will_appear_here')")

    // Loading State
    div(v-else-if="isLoadingVisits")
      yz-skeleton-loader(type="table-thead")
      yz-skeleton-loader(type="table-tbody")

    // Main Content
    yz-data-table(v-else :headers="visitsHeaders" :items="visitsData" item-key="uid" disable-sort)
      template(v-slot:item.name="{ item }")
        span(:class="item.name === 'N/A' ? 'text-neutral-500' : ''") {{ item.name }}

      template(v-slot:item.nric="{ item }")
        span(:class="item.nric === 'N/A' ? 'text-neutral-500' : ''") {{ item.nric }}

      template(v-slot:item.staffId="{ item }")
        span(:class="item.staffId === 'N/A' ? 'text-neutral-500' : ''") {{ item.staffId }}

      template(v-slot:item.visitNote="{ item }")
        span(:class="item.visitNote === 'N/A' ? 'text-neutral-500' : ''") {{ item.visitNote }}

      template(v-slot:item.diagnosis="{ item }")
        span(:class="item.diagnosis === 'N/A' ? 'text-neutral-500' : ''") {{ item.diagnosis }}

      template(v-slot:item.doctor="{ item }")
        span(:class="item.doctor === 'N/A' ? 'text-neutral-500' : ''") {{ item.doctor }}

      template(v-slot:item.claimStatus="{ item }")
        div(v-if="item.claimStatus === 'N/A'")
          span.text-neutral-500 N/A
        yz-badge(v-else size="sm" :color="getClaimStatusColor(item.claimStatus)" :text-color="getClaimStatusTextColor(item.claimStatus)")
          yz-dot(:color="getClaimStatusDotColor(item.claimStatus)" size="6px")
          span.ml-2.capitalize {{ item.claimStatus }}

      template(v-slot:item.actions="{ item }")
        yz-menu(offset-y close-on-click left)
          template(v-slot:activator="{ on, attrs }")
            yz-btn(icon-only, icon-color="yz-neutral-500" v-bind="attrs" v-on="on" icon="$yzi-dots-horizontal")
          yz-list
            yz-list-item(@click="onViewInvoice(item)")
              div {{ $t('panel.view_invoice') }}
            yz-divider
            yz-list-item(v-if="item.panel_claim" @click="onViewClaim(item)")
              div {{ $t('panel.view_claim') }}

    yz-pagination.mt-4(v-if="!isError && visitsData.length > 0" v-model="visitsTableOptions.page" :page-size.sync="visitsTableOptions.itemsPerPage" :count="visitsTableOptions.totalItems" type="item" arrow, @input="fetchPanelVisits")
</template>

<script>
import { mapActions } from 'vuex'
import { DateTime } from 'luxon'

export default {
  props: {
    parentPanelId: {
      type: [Number, String],
      required: true
    }
  },
  data() {
    return {
      isLoadingVisits: false,
      isError: false,

      visitsHeaders: [
        { text: this.$t('panel.name'), value: 'name' },
        { text: this.$t('panel.nric'), value: 'nric' },
        { text: this.$t('panel.staff_id_field'), value: 'staffId' },
        { text: this.$t('panel.visit_date'), value: 'visitDate' },
        { text: this.$t('panel.visit_note'), value: 'visitNote' },
        { text: this.$t('panel.diagnosis'), value: 'diagnosis' },
        { text: this.$t('panel.doctor'), value: 'doctor' },
        { text: this.$t('panel.claim_status'), value: 'claimStatus' },
        { text: '', value: 'actions', align: 'end', sortable: false }
      ],
      visitsTableOptions: {
        page: 1,
        itemsPerPage: 10,
        totalItems: 0
      },
      visitsData: []
    }
  },
  mounted() {
    if (this.parentPanelId) {
      this.fetchPanelVisits()
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    async fetchPanelVisits() {
      this.isLoadingVisits = true
      this.isError = false
      const params = {
        page: this.visitsTableOptions.page,
        page_size: this.visitsTableOptions.itemsPerPage,
        ordering: '-created'
      }

      try {
        const response = await this.$axios.get(`/panels/${this.parentPanelId}/visits`, { params })
        this.visitsData = response.data.results.map(visit => ({
          ...visit,
          nric: visit.ic || 'N/A',
          name: visit.name || 'N/A',
          staffId: visit.staff_id || 'N/A',
          visitDate: this.formatDate(visit.visit_date),
          visitNote: visit.visit_note || 'N/A',
          diagnosis: visit.diagnosis?.length > 0 ? visit.diagnosis.join(', ') : 'N/A',
          doctor: visit.doctor?.name || 'N/A',
          claimStatus: this.getClaimStatus(visit)
        }))
        this.visitsTableOptions.totalItems = response.data.count
      } catch (error) {
        console.error('Error fetching panel visits:', error)
        this.isError = true
        await this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isLoadingVisits = false
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = DateTime.fromISO(dateString)
      return date.toFormat('d MMMM yyyy')
    },
    getClaimStatus(visit) {
      const claimStatus = visit.claim_status

      if (claimStatus === 'new' && visit.submission_due) {
        const submissionDue = DateTime.fromISO(visit.submission_due)
        const now = DateTime.local()

        if (submissionDue < now) {
          return 'overdue'
        } else {
          return 'pending'
        }
      }

      return claimStatus || 'N/A'
    },
    getClaimStatusColor(status) {
      const statusColors = {
        pending: '#F9FAFB',
        overdue: '#848484',
        submitted: '#FFF7D9',
        rejected: '#FFEBEB',
        approved: '#F0F8FF',
        received: '#EEFCE9'
      }
      return statusColors[status] || 'neutral-200'
    },
    getClaimStatusDotColor(status) {
      const statusColors = {
        pending: '#848484',
        overdue: 'white',
        submitted: '#F2BE37',
        rejected: '#F04438',
        approved: '#0C6DFF',
        received: '#14AE5C'
      }
      return statusColors[status] || 'neutral-200'
    },
    getClaimStatusTextColor(status) {
      const textColors = {
        pending: '#333333',
        overdue: 'white',
        submitted: '#CA6C16',
        rejected: '#B52222',
        approved: '#3570CA',
        received: '#1A7935'
      }
      return textColors[status] || 'neutral-500'
    },
    onViewInvoice(visit) {
      const route = this.$router.resolve(`/admin/clinic/checkout/${visit.lead_pid}`)
      window.open(route.href, '_blank')
    },
    onViewClaim(visit) {
      if (!visit.panel_claim) {
        this.showMessage(['error', this.$t('panel.no_claim_associated')])
        return
      }
      if (!visit.panel_claim.id) {
        this.showMessage(['error', this.$t('panel.no_claim_associated')])
        return
      }
      const route = this.$router.resolve(`/admin/clinic/panels/claim/${visit.panel_claim.id}`)
      window.open(route.href, '_blank')
    }
  }
}
</script>

<style scoped></style>
